import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories, topics } from "@/mocks/news";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<"category" | "topic" | null>(null);
  const [keyword, setKeyword] = useState("");

  const goSearch = (e?: FormEvent) => {
    e?.preventDefault();
    navigate(`/search?q=${encodeURIComponent(keyword.trim())}`);
    setMenuOpen(false);
    setOpenMenu(null);
  };

  const navLink = (path: string, label: string) => {
    const active =
      path === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(path);
    return (
      <Link
        key={path}
        to={path}
        onClick={() => {
          setMenuOpen(false);
          setOpenMenu(null);
        }}
        className={`px-3 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
          active
            ? "text-primary-600 font-bold"
            : "text-foreground-700 hover:text-primary-600"
        }`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-6">
        <div className="h-16 flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0 cursor-pointer"
          >
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xl">
              <i className="ri-flashlight-fill" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-xl font-heading font-black text-foreground-950 tracking-tight">
                51爆料
              </span>
              <span className="text-[10px] text-foreground-500 tracking-widest uppercase">
                BaoLiao News
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 ml-2">
            {navLink("/", "首页")}

            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("category")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button
                type="button"
                onClick={() =>
                  setOpenMenu(openMenu === "category" ? null : "category")
                }
                className={`px-3 py-2 text-sm rounded-md transition-colors cursor-pointer flex items-center gap-1 ${
                  location.pathname.startsWith("/category")
                    ? "text-primary-600 font-bold"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                分类
                <i className="ri-arrow-down-s-line" />
              </button>
              {openMenu === "category" && (
                <div className="absolute left-0 top-full pt-2 w-56">
                  <div className="bg-background-50 border border-background-200 rounded-lg p-2">
                    {categories.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/category/${c.slug}`}
                        onClick={() => setOpenMenu(null)}
                        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-background-100 cursor-pointer"
                      >
                        <i className={`${c.icon} text-lg text-primary-500`} />
                        <span className="flex flex-col">
                          <span className="text-sm font-medium text-foreground-900">
                            {c.name}
                          </span>
                          <span className="text-xs text-foreground-500">
                            {c.subs.join(" · ")}
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("topic")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button
                type="button"
                onClick={() =>
                  setOpenMenu(openMenu === "topic" ? null : "topic")
                }
                className={`px-3 py-2 text-sm rounded-md transition-colors cursor-pointer flex items-center gap-1 ${
                  location.pathname.startsWith("/topic")
                    ? "text-primary-600 font-bold"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                专题
                <i className="ri-arrow-down-s-line" />
              </button>
              {openMenu === "topic" && (
                <div className="absolute left-0 top-full pt-2 w-64">
                  <div className="bg-background-50 border border-background-200 rounded-lg p-2">
                    {topics.map((t) => (
                      <Link
                        key={t.id}
                        to={`/topic/${t.id}`}
                        onClick={() => setOpenMenu(null)}
                        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-links-line text-lg text-accent-600" />
                        <span className="text-sm text-foreground-900">
                          {t.title}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLink("/about", "关于")}
            {navLink("/contact", "联系")}
          </nav>

          {/* Desktop search */}
          <form
            onSubmit={goSearch}
            className="hidden md:flex items-center ml-auto bg-background-100 border border-background-200 rounded-full px-3 h-10 w-64"
          >
            <i className="ri-search-line text-foreground-400 text-base" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索爆料关键词"
              className="flex-1 bg-transparent outline-none text-sm px-2 text-foreground-800 placeholder:text-foreground-400"
            />
          </form>

          {/* Mobile buttons */}
          <div className="flex md:hidden items-center gap-2 ml-auto">
            <Link
              to="/search"
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-xl" />
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-900 cursor-pointer"
              aria-label="菜单"
            >
              <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-in">
          <form
            onSubmit={goSearch}
            className="flex items-center bg-background-100 border border-background-200 rounded-full px-3 h-10 mb-3"
          >
            <i className="ri-search-line text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索爆料关键词"
              className="flex-1 bg-transparent outline-none text-sm px-2"
            />
          </form>

          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="px-3 py-2 rounded-md text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              首页
            </Link>
            <p className="px-3 pt-2 pb-1 text-xs text-foreground-400">分类</p>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                onClick={() => setMenuOpen(false)}
                className="px-3 py-2 rounded-md text-sm text-foreground-800 hover:bg-background-100 cursor-pointer flex items-center gap-2"
              >
                <i className={`${c.icon} text-primary-500`} />
                {c.name}爆料
              </Link>
            ))}
            <p className="px-3 pt-2 pb-1 text-xs text-foreground-400">专题</p>
            {topics.map((t) => (
              <Link
                key={t.id}
                to={`/topic/${t.id}`}
                onClick={() => setMenuOpen(false)}
                className="px-3 py-2 rounded-md text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
              >
                {t.title}
              </Link>
            ))}
            <div className="border-t border-background-200 mt-2 pt-2 flex flex-col gap-1">
              <Link
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="px-3 py-2 rounded-md text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
              >
                关于我们
              </Link>
              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
                className="px-3 py-2 rounded-md text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
              >
                联系反馈
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}